import { Component, Input, OnInit, NgZone, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { Event } from 'src/app/core/models/events-response';
import { EventService } from 'src/app/core/services/event.service';
import { MobileService } from 'src/app/core/services/mobile-service';
import { StringSimilarityService } from 'src/app/core/services/string-similarity.service';
import { filterArray, Options } from 'src/app/core/models/filter';
import { Router } from '@angular/router';
import { FilterService } from 'src/app/core/services/filter.service';

declare function registerEvent(eventId, time): void;

class EventSimilarity {
  event: Event;
  similarity: number;
  constructor(event: Event, similarity: number) {
    this.event = event;
    this.similarity = similarity;
  }
}

@Component({
  selector: 'app-event-details',
  templateUrl: './event-details.page.html',
  styleUrls: ['./event-details.page.scss'],
})
export class EventDetailsPage implements OnInit {
  eventSource: any;
  id: any;
  marker: any;
  mapUrl: string;
  similarEvents: Event[];
  buttonClicked: boolean = false;
  filtersLoaded: boolean = false;

  @Input()
  event: Event;
  price: string;
  filter = filterArray;

  constructor(
    private eventService: EventService,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private mobileService: MobileService,
    private stringSimilarityService: StringSimilarityService,
    private router: Router,
    private filterService: FilterService
  ) {
    // Assign URL parameters to get correct event details
    this.eventSource = this.route.snapshot.paramMap.get('eventSource');
    this.id = this.route.snapshot.paramMap.get('id');
    this.eventService
      .getEvents()
      .then((eventSummaries: Event[]) => {
        this.filter = this.filterService.buildDefaultFilters(eventSummaries);
        this.filtersLoaded = true;
      })
      .catch((err) => {});
    this.getEventDetails(this.eventSource, this.id);
  }

  //Called when filter is applied
  applyFilters(filters) {
    this.filterService.setAppliedFilters(filters);
    this.router.navigate(['/']);
  }

  //Called when search term is entered
  searchEvents(searchTerm: string) {
    this.filterService.setSearchTerm(searchTerm);
    this.router.navigate(['/']);
  }

  ngOnInit() {}

  // Get event details using backend API call
  private getEventDetails(eventSource: string, eventId: string) {
    this.eventService
      .getEventDetails(eventSource, eventId)
      .then((eventDetails: Event) => {
        this.event = new Event(eventDetails);
        if (this.event.location) {
          this.mapUrl = `http://maps.google.com/maps?z=12&t=m&q=loc:${this.event.location.latitude}+${this.event.location.longitude}`;
        }

        //Find similar events
        this.eventService
          .getEvents()
          .then((events: Event[]) => {
            let eventSimilarities: EventSimilarity[] = [];

            events.forEach((event) => {
              if (event.eventId == this.event.eventId) return;
              if (event.isSoldOut) return;
              if (moment.tz(event.startDateTime, 'Pacific/Auckland').valueOf() < moment().tz('Pacific/Auckland').valueOf()) return;

              let similarity = this.stringSimilarityService.similarity(event.name, this.event.name);
              let eventSimilarity = new EventSimilarity(event, similarity);

              if (this.event.categoryName && event.categoryName) {
                if (this.event.categoryName == event.categoryName) {
                  eventSimilarities.push(eventSimilarity);
                }
              } else if (this.event.organisationName && event.organisationName) {
                if (this.event.organisationName == event.organisationName) {
                  eventSimilarities.push(eventSimilarity);
                }
              } else {
                eventSimilarities.push(eventSimilarity);
              }
            });

            this.similarEvents = eventSimilarities
              .sort((a, b) => b.similarity - a.similarity)
              .map((eventSimilarity) => eventSimilarity.event);
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  checkout() {
    var date = new Date();
    var time = date.getTime();

    this.ngZone.runOutsideAngular(() => {
      registerEvent(this.event.eventId, time);
    });
  }

  @HostListener('window:popstate')
  onPopState() {
    if (this.event) {
      var frame = document.getElementById(`eventbrite-widget-modal-${this.event.eventId}`);
      if (frame) {
        frame.parentNode.removeChild(frame);
        var eventbriteWidget = document.getElementById('eventbrite-widget-modal-overlay');
        eventbriteWidget.setAttribute('style', 'opacity: 0;');
      }
    }
  }

  getBackgroundStyle() {
    const styles = { 'background-image': 'url(' + this.event.imageUrl + ')' };
    return styles;
  }

  isMobile(): boolean {
    return this.mobileService.isMobile();
  }

  isDesktop(): boolean {
    return this.mobileService.isDesktop();
  }

  getCurrentUrl() {
    return window.location.href;
  }

  getEventDate() {
    if (this.event.startDateTime) {
      return moment(this.event.startDateTime).format('dddd D MMM YYYY');
    } else {
      return undefined;
    }
  }

  getEventTime() {
    if (this.event.startDateTime && this.event.endDateTime) {
      if (this.event.startDateTime == this.event.endDateTime) {
        return moment(this.event.startDateTime).format('h:mma');
      } else {
        return (
          moment(this.event.startDateTime).format('h:mma') + ' - ' + moment(this.event.endDateTime).format('h:mma')
        );
      }
    } else if (this.event.startDateTime) {
      return moment(this.event.startDateTime).format('h:mma');
    } else {
      return undefined;
    }
  }

  getSimilarDisplayCount() {
    return this.mobileService.getDisplayedCardsPerRow();
  }

  openBreadcrumb() {
    this.buttonClicked = true;
  }

  closeBreadcrumb() {
    this.buttonClicked = false;
  }
}
